<template>
  <div class="admin-manage-events">
    <div class="manage-panel">
      <div class="manage-header">
        <div>
          <h5>Manage Events</h5>
          <div class="result-count">{{ filteredEvents.length }} events shown</div>
        </div>
        <b-button size="sm" variant="outline-secondary" @click="getEvents" :disabled="loading">
          <i class="fas fa-sync-alt mr-1"></i>Refresh
        </b-button>
      </div>

      <div class="filters">
        <b-form-group label="Starting date" label-for="manage-events-date">
          <b-form-datepicker
            id="manage-events-date"
            v-model="fromDate"
            today-button
            locale="en-US"
            @input="getEvents"
          ></b-form-datepicker>
        </b-form-group>
        <b-form-group label="Filter by event name" label-for="manage-events-search">
          <b-form-input
            id="manage-events-search"
            v-model.trim="search"
            placeholder="Search events"
          ></b-form-input>
        </b-form-group>
      </div>

      <b-table
        responsive
        small
        striped
        hover
        show-empty
        :busy="loading"
        :items="filteredEvents"
        :fields="fields"
        empty-text="No events found from this date."
      >
        <template #table-busy>
          <div class="text-center py-4">
            <b-spinner small class="mr-2"></b-spinner>Loading events
          </div>
        </template>

        <template #cell(event_date)="data">
          <span class="date-cell">{{ data.item.event_date }}</span>
        </template>

        <template #cell(time)="data">
          {{ displayTime(data.item.start_time) }} - {{ displayTime(data.item.end_time) }}
        </template>

        <template #cell(event_type_id)="data">
          {{ data.item.event_type_id === 1 ? 'Open Play' : 'Court Rental' }}
        </template>

        <template #cell(max_member)="data">
          {{ data.item.max_member === 0 ? 'Unlimited' : data.item.max_member }}
        </template>

        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" @click="openEdit(data.item)">
            <i class="fas fa-edit mr-1"></i>Edit
          </b-button>
        </template>
      </b-table>
    </div>

    <b-modal
      ref="editEventModal"
      size="lg"
      centered
      scrollable
      title="Edit Event"
      hide-footer
      @hidden="closeEdit"
    >
      <b-form v-if="editingEvent" @submit.prevent="saveEvent">
        <b-alert :show="!!formError" variant="danger">{{ formError }}</b-alert>

        <b-row>
          <b-col md="8">
            <b-form-group label="Event name" label-for="edit-event-name">
              <b-form-input id="edit-event-name" v-model.trim="editingEvent.name" maxlength="100" required></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Event type" label-for="edit-event-type">
              <b-form-select id="edit-event-type" v-model.number="editingEvent.event_type_id" :options="eventTypes"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <b-form-group label="Date" label-for="edit-event-date">
              <b-form-input id="edit-event-date" type="date" v-model="editingEvent.event_date" required></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Start time" label-for="edit-start-time">
              <b-form-input id="edit-start-time" type="time" v-model="editingEvent.start_time" required></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="End time" label-for="edit-end-time">
              <b-form-input id="edit-end-time" type="time" v-model="editingEvent.end_time" required></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Description (HTML)" label-for="edit-event-description">
          <b-form-textarea id="edit-event-description" v-model="editingEvent.description" rows="4"></b-form-textarea>
        </b-form-group>

        <b-row>
          <b-col md="4">
            <b-form-group label="Center ID" label-for="edit-center-id">
              <b-form-input id="edit-center-id" type="number" min="1" v-model.number="editingEvent.center_id" required></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Courts" label-for="edit-courts">
              <b-form-input id="edit-courts" v-model.trim="editingEvent.court" maxlength="50" required></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Reservable courts" label-for="edit-reservable-courts">
              <b-form-input id="edit-reservable-courts" type="number" min="0" v-model.number="editingEvent.court_reservable"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <b-form-group label="Minimum reservation (minutes)" label-for="edit-min-reserve">
              <b-form-input id="edit-min-reserve" type="number" min="0" v-model.number="editingEvent.min_reserve_time"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Reservation interval (minutes)" label-for="edit-reserve-interval">
              <b-form-input id="edit-reserve-interval" type="number" min="0" v-model.number="editingEvent.reserve_interval"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Interval price" label-for="edit-interval-price">
              <b-form-input id="edit-interval-price" type="number" min="0" step="0.01" v-model.number="editingEvent.reserve_interval_price"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <b-form-group label="Drop-in price" label-for="edit-event-price">
              <b-form-input id="edit-event-price" type="number" min="0" step="0.01" v-model.number="editingEvent.event_price"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Maximum players (0 = unlimited)" label-for="edit-max-member">
              <b-form-input id="edit-max-member" type="number" min="0" v-model.number="editingEvent.max_member"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Backup capacity" label-for="edit-max-backup">
              <b-form-input id="edit-max-backup" type="number" min="0" v-model.number="editingEvent.max_backup"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="modal-actions">
          <b-button variant="outline-danger" @click="confirmDeleteEvent" :disabled="saving || deleting">
            <b-spinner v-if="deleting" small class="mr-1"></b-spinner>
            <i v-else class="fas fa-trash-alt mr-1"></i>Delete
          </b-button>
          <div class="modal-primary-actions">
            <b-button variant="outline-secondary" @click="$refs.editEventModal.hide()" :disabled="saving || deleting">Cancel</b-button>
            <b-button type="submit" variant="primary" :disabled="saving || deleting">
              <b-spinner v-if="saving" small class="mr-1"></b-spinner>
              Save Changes
            </b-button>
          </div>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import datetime_mixin from '../../mixins/datetime.js'

export default {
  mixins: [datetime_mixin],
  data() {
    return {
      events: [],
      editingEvent: null,
      fromDate: '',
      search: '',
      loading: false,
      saving: false,
      deleting: false,
      formError: '',
      eventTypes: [
        { value: 1, text: 'Open Play' },
        { value: 2, text: 'Court Rental' },
      ],
      fields: [
        { key: 'event_date', label: 'Date', sortable: true },
        { key: 'time', label: 'Time' },
        { key: 'name', label: 'Event', sortable: true },
        { key: 'event_type_id', label: 'Type', sortable: true },
        { key: 'court', label: 'Courts' },
        { key: 'max_member', label: 'Capacity' },
        { key: 'actions', label: '', tdClass: 'action-cell' },
      ],
    }
  },
  computed: {
    filteredEvents () {
      const query = this.search.toLowerCase();
      if (!query) return this.events;
      return this.events.filter(event => event.name.toLowerCase().includes(query));
    },
  },
  mounted () {
    this.fromDate = this.GetDate(0);
    this.getEvents();
  },
  methods: {
    displayTime (time) {
      return time ? this.H24toH12(time) : '';
    },
    getEvents () {
      if (!this.fromDate) return;
      this.loading = true;
      this.$http.get('/api/admin/events?from=' + encodeURIComponent(this.fromDate)
      ).then((resp) => {
        this.events = resp.data.events || [];
      }).catch((error) => {
        alert(error.response.data.message);
      }).finally(() => {
        this.loading = false;
      })
    },
    openEdit (event) {
      this.formError = '';
      this.editingEvent = { ...event };
      this.$nextTick(() => this.$refs.editEventModal.show());
    },
    closeEdit () {
      this.editingEvent = null;
      this.formError = '';
    },
    validateEvent () {
      const event = this.editingEvent;
      if (!event.name || !event.name.trim()) return 'Event name is required.';
      if (!event.event_date) return 'Event date is required.';
      if (!event.start_time || !event.end_time) return 'Start and end times are required.';
      if (event.end_time <= event.start_time) return 'End time must be after start time.';
      if (!event.court || !event.court.trim()) return 'Court is required.';
      return '';
    },
    saveEvent () {
      this.formError = this.validateEvent();
      if (this.formError) return;

      this.saving = true;
      this.$http.post('/api/admin/update_event', this.editingEvent
      ).then(() => {
        this.$refs.editEventModal.hide();
        this.getEvents();
        this.$bvToast.toast('Event updated.', {
          title: 'Manage Events',
          variant: 'success',
          solid: true,
        });
      }).catch((error) => {
        this.formError = error.response.data.message;
      }).finally(() => {
        this.saving = false;
      })
    },
    confirmDeleteEvent () {
      const event = this.editingEvent;
      if (!event) return;

      this.$bvModal.msgBoxConfirm(
        `Delete "${event.name}" on ${event.event_date}? This will also remove all reservations and check-ins for this event.`,
        {
          title: 'Delete Event',
          okTitle: 'Delete Event',
          okVariant: 'danger',
          cancelTitle: 'Keep Event',
          centered: true,
        }
      ).then((confirmed) => {
        if (confirmed) this.deleteEvent(event.id);
      })
    },
    deleteEvent (eventId) {
      this.formError = '';
      this.deleting = true;
      this.$http.post('/api/admin/delete_event', { id: eventId }
      ).then(() => {
        this.$refs.editEventModal.hide();
        this.getEvents();
        this.$bvToast.toast('Event deleted.', {
          title: 'Manage Events',
          variant: 'success',
          solid: true,
        });
      }).catch((error) => {
        this.formError = error.response && error.response.data
          ? error.response.data.message
          : 'Unable to delete the event.';
      }).finally(() => {
        this.deleting = false;
      })
    },
  },
}
</script>

<style scoped lang="scss">
.admin-manage-events {
  padding: 20px 12px 60px;
  color: #343a40;
}

.manage-panel {
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 20px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #d9dde1;
}

.manage-header,
.filters,
.modal-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.manage-header {
  margin-bottom: 18px;
}

.manage-header h5 {
  margin: 0;
}

.result-count {
  margin-top: 4px;
  color: #6c757d;
  font-size: 12px;
}

.filters {
  align-items: flex-end;
  justify-content: flex-start;
  margin-bottom: 10px;
}

.filters .form-group {
  width: 260px;
  margin-bottom: 10px;
  text-align: left;
}

.date-cell {
  white-space: nowrap;
}

.modal-actions {
  padding-top: 10px;
  border-top: 1px solid #dee2e6;
}

.modal-primary-actions {
  display: flex;
  gap: 8px;
}

@media (max-width: 600px) {
  .manage-panel {
    padding: 14px;
  }

  .manage-header,
  .filters {
    align-items: stretch;
    flex-direction: column;
  }

  .filters .form-group {
    width: 100%;
  }
}
</style>
